import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  Close,
  DeleteSweepOutlined,
  EditNoteOutlined,
  FormatBold,
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInFull,
} from "@mui/icons-material";

const NOTES_STORAGE_KEY = "preezie-cms-notes";
const NOTES_FORMAT_STORAGE_KEY = "preezie-cms-notes-format";
const RICH_TEXT_FORMAT = "rich-text-v1";
const VIEWPORT_MARGIN = 16;
const DEFAULT_PANEL_WIDTH = 360;
const DEFAULT_PANEL_HEIGHT = 400;
const MIN_PANEL_WIDTH = 150;
const MIN_PANEL_HEIGHT = 120;
const LAUNCHER_FADE_DELAY = 4000;

const constrainGeometry = (geometry) => {
  const maxWidth = Math.max(0, window.innerWidth - VIEWPORT_MARGIN * 2);
  const maxHeight = Math.max(0, window.innerHeight - VIEWPORT_MARGIN * 2);
  const minWidth = Math.min(MIN_PANEL_WIDTH, maxWidth);
  const minHeight = Math.min(MIN_PANEL_HEIGHT, maxHeight);
  const width = Math.min(Math.max(geometry.width, minWidth), maxWidth);
  const height = Math.min(Math.max(geometry.height, minHeight), maxHeight);
  const maxX = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
  const maxY = Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);

  return {
    width,
    height,
    x: Math.min(Math.max(geometry.x, VIEWPORT_MARGIN), maxX),
    y: Math.min(Math.max(geometry.y, VIEWPORT_MARGIN), maxY),
  };
};

const getInitialGeometry = () => {
  const width = Math.min(
    DEFAULT_PANEL_WIDTH,
    window.innerWidth - VIEWPORT_MARGIN * 2
  );
  const height = Math.min(
    DEFAULT_PANEL_HEIGHT,
    window.innerHeight - 104
  );

  return constrainGeometry({
    width,
    height,
    x: window.innerWidth - width - 24,
    y: window.innerHeight - height - 96,
  });
};

const escapePlainText = (value) => {
  const container = document.createElement("div");
  container.textContent = value;
  return container.innerHTML.replace(/\n/g, "<br>");
};

const isSafeColor = (value) =>
  /^(#[0-9a-f]{3,8}|rgba?\([\d\s,%.]+\)|[a-z]+)$/i.test(value);

const isSafeFontSize = (value) =>
  /^(\d+(\.\d+)?(px|em|rem|pt|%)|xx-small|x-small|small|medium|large|x-large|xx-large)$/i.test(
    value
  );

const sanitizeNotesHtml = (value) => {
  const template = document.createElement("template");
  const output = document.createElement("div");
  const allowedTags = new Set(["b", "strong", "i", "em", "u", "span", "font", "div", "p", "br"]);

  template.innerHTML = value;

  const appendCleanNode = (node, parent) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parent.appendChild(document.createTextNode(node.textContent || ""));
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tagName = node.tagName.toLowerCase();
    if (tagName === "script" || tagName === "style") return;

    const destination = allowedTags.has(tagName)
      ? document.createElement(tagName)
      : parent;

    if (destination !== parent) {
      const color = node.style.color || node.getAttribute("color");
      const fontSize = node.style.fontSize;
      const fontWeight = node.style.fontWeight;
      const fontStyle = node.style.fontStyle;
      const textDecoration = node.style.textDecoration;
      const legacySize = node.getAttribute("size");

      if (color && isSafeColor(color)) destination.style.color = color;
      if (fontSize && isSafeFontSize(fontSize)) {
        destination.style.fontSize = fontSize;
      }
      if (/^(normal|bold|[1-9]00)$/i.test(fontWeight)) {
        destination.style.fontWeight = fontWeight;
      }
      if (/^(normal|italic)$/i.test(fontStyle)) destination.style.fontStyle = fontStyle;
      if (/^(none|underline|line-through)$/i.test(textDecoration)) {
        destination.style.textDecoration = textDecoration;
      }
      if (tagName === "font" && /^[1-7]$/.test(legacySize || "")) {
        destination.setAttribute("size", legacySize);
      }

      parent.appendChild(destination);
    }

    Array.from(node.childNodes).forEach((child) => appendCleanNode(child, destination));
  };

  Array.from(template.content.childNodes).forEach((node) => appendCleanNode(node, output));
  return output.innerHTML;
};

const getNotesText = (html) => {
  const container = document.createElement("div");
  container.innerHTML = html;
  return container.textContent || "";
};

const readSavedNotes = () => {
  try {
    const savedNotes = window.localStorage.getItem(NOTES_STORAGE_KEY) || "";
    const savedFormat = window.localStorage.getItem(NOTES_FORMAT_STORAGE_KEY);

    return savedFormat === RICH_TEXT_FORMAT
      ? sanitizeNotesHtml(savedNotes)
      : escapePlainText(savedNotes);
  } catch {
    return "";
  }
};

const FloatingNotes = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isToolbarVisible, setIsToolbarVisible] = useState(true);
  const [notes, setNotes] = useState(readSavedNotes);
  const [geometry, setGeometry] = useState(getInitialGeometry);
  const [isLauncherIdle, setIsLauncherIdle] = useState(false);
  const [isClearConfirmationOpen, setIsClearConfirmationOpen] = useState(false);
  const editorRef = useRef(null);
  const interactionRef = useRef(null);
  const selectionRef = useRef(null);
  const wasOpenRef = useRef(false);
  const launcherFadeTimerRef = useRef(null);

  const keepLauncherVisible = useCallback(() => {
    window.clearTimeout(launcherFadeTimerRef.current);
    setIsLauncherIdle(false);
  }, []);

  const scheduleLauncherFade = useCallback(() => {
    keepLauncherVisible();
    if (!isOpen) {
      launcherFadeTimerRef.current = window.setTimeout(
        () => setIsLauncherIdle(true),
        LAUNCHER_FADE_DELAY
      );
    }
  }, [isOpen, keepLauncherVisible]);

  useEffect(() => {
    try {
      const cleanNotes = sanitizeNotesHtml(notes);
      window.localStorage.setItem(NOTES_STORAGE_KEY, cleanNotes);
      window.localStorage.setItem(NOTES_FORMAT_STORAGE_KEY, RICH_TEXT_FORMAT);
    } catch {
      // Keep notes available for the current session if browser storage is blocked.
    }
  }, [notes]);

  useEffect(() => {
    if (isOpen) {
      editorRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    scheduleLauncherFade();
    return () => window.clearTimeout(launcherFadeTimerRef.current);
  }, [scheduleLauncherFade]);

  useEffect(() => {
    if (
      isOpen &&
      !wasOpenRef.current &&
      editorRef.current
    ) {
      editorRef.current.innerHTML = notes;
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, notes]);

  useEffect(() => {
    const keepPanelInViewport = () => {
      setGeometry((current) => constrainGeometry(current));
    };

    window.addEventListener("resize", keepPanelInViewport);
    return () => window.removeEventListener("resize", keepPanelInViewport);
  }, []);

  const startInteraction = useCallback((event, type) => {
    if (event.button !== 0) return;
    if (type === "drag" && event.target.closest("button")) return;

    event.preventDefault();
    interactionRef.current = {
      type,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      geometry,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [geometry]);

  const updateInteraction = useCallback((event) => {
    const interaction = interactionRef.current;
    if (!interaction || interaction.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - interaction.startX;
    const deltaY = event.clientY - interaction.startY;

    setGeometry(
      constrainGeometry(
        interaction.type === "drag"
          ? {
              ...interaction.geometry,
              x: interaction.geometry.x + deltaX,
              y: interaction.geometry.y + deltaY,
            }
          : {
              ...interaction.geometry,
              width: interaction.geometry.width + deltaX,
              height: interaction.geometry.height + deltaY,
            }
      )
    );
  }, []);

  const endInteraction = useCallback((event) => {
    if (interactionRef.current?.pointerId === event.pointerId) {
      interactionRef.current = null;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  }, []);

  const resizeWithKeyboard = (event) => {
    const step = event.shiftKey ? 40 : 20;
    const sizeChange = {
      ArrowLeft: { width: -step, height: 0 },
      ArrowRight: { width: step, height: 0 },
      ArrowUp: { width: 0, height: -step },
      ArrowDown: { width: 0, height: step },
    }[event.key];

    if (!sizeChange) return;
    event.preventDefault();
    setGeometry((current) =>
      constrainGeometry({
        ...current,
        width: current.width + sizeChange.width,
        height: current.height + sizeChange.height,
      })
    );
  };

  const saveSelection = useCallback(() => {
    const selection = window.getSelection();
    const editor = editorRef.current;
    if (!editor || !selection?.rangeCount) return;

    const range = selection.getRangeAt(0);
    if (editor.contains(range.commonAncestorContainer)) {
      selectionRef.current = range.cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    const editor = editorRef.current;
    const range = selectionRef.current;
    if (!editor || !range) return;

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, []);

  const syncNotesFromEditor = useCallback(() => {
    setNotes(sanitizeNotesHtml(editorRef.current?.innerHTML || ""));
  }, []);

  const applyFormatting = useCallback((command, value) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    restoreSelection();
    document.execCommand?.("styleWithCSS", false, true);
    document.execCommand?.(command, false, value);
    saveSelection();
    syncNotesFromEditor();
  }, [restoreSelection, saveSelection, syncNotesFromEditor]);

  const clearNotes = () => {
    if (editorRef.current) editorRef.current.innerHTML = "";
    setNotes("");
    setIsClearConfirmationOpen(false);
  };

  const pastePlainText = (event) => {
    event.preventDefault();
    const plainText = event.clipboardData.getData("text/plain");
    document.execCommand?.("insertText", false, plainText);
    syncNotesFromEditor();
  };

  const isCompactLayout = geometry.width <= 320 || geometry.height <= 280;
  const isToolbarCollapsedForSpace =
    geometry.width <= 230 || geometry.height <= 160;
  const shouldShowToolbar = isToolbarVisible && !isToolbarCollapsedForSpace;

  return (
    <>
      {isOpen && (
        <Paper
          role="region"
          aria-label="CMS notes"
          elevation={0}
          sx={{
            position: "fixed",
            left: geometry.x,
            top: geometry.y,
            zIndex: 1300,
            width: geometry.width,
            height: geometry.height,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: "20px",
            border: "1px solid rgba(255, 132, 76, 0.18)",
            backgroundColor: "rgba(255, 255, 255, 0.97)",
            backdropFilter: "blur(18px)",
            boxShadow: "0 24px 70px rgba(83, 52, 32, 0.22)",
            pointerEvents: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              pl: 0.75,
              pr: 1,
              py: 0.75,
              borderBottom: "1px solid rgba(255, 132, 76, 0.12)",
              background:
                "linear-gradient(135deg, rgba(255, 247, 241, 0.98), rgba(255, 255, 255, 0.98))",
              cursor: "move",
              touchAction: "none",
              userSelect: "none",
            }}
            data-testid="notes-drag-handle"
            aria-label="Move notes"
            onPointerDown={(event) => startInteraction(event, "drag")}
            onPointerMove={updateInteraction}
            onPointerUp={endInteraction}
            onPointerCancel={endInteraction}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                borderRadius: "12px",
                color: "#f97316",
                backgroundColor: "#fff1e8",
              }}
            >
              <EditNoteOutlined />
            </Box>

            {!isCompactLayout && (
              <Box
                sx={{
                  minWidth: 0,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  component="h2"
                  sx={{
                    color: "#202531",
                    fontSize: "1rem",
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  My notes
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "#6b7280",
                    whiteSpace: "nowrap",
                  }}
                >
                  <CheckCircleOutline sx={{ fontSize: 14, color: "#22a06b" }} />
                  Saved automatically
                </Typography>
              </Box>
            )}

            {isCompactLayout && <Box sx={{ flex: 1 }} />}

            <Tooltip title="Clear notes">
              <span>
                <IconButton
                  aria-label="Clear notes"
                  onClick={() => setIsClearConfirmationOpen(true)}
                  disabled={!getNotesText(notes)}
                  size="small"
                  sx={{
                    width: 28,
                    height: 28,
                    color: "#6b7280",
                    "&:hover": {
                      color: "#ef4444",
                      backgroundColor: "rgba(239, 68, 68, 0.08)",
                    },
                  }}
                >
                  <DeleteSweepOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Close notes">
              <IconButton
                aria-label="Close notes"
                onClick={() => setIsOpen(false)}
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  color: "#6b7280",
                  "&:hover": {
                    color: "#f97316",
                    backgroundColor: "rgba(249, 115, 22, 0.08)",
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {isClearConfirmationOpen && (
            <Box
              role="alertdialog"
              aria-label="Confirm deleting notes"
              sx={{
                position: "absolute",
                top: 48,
                right: 8,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1,
                py: 0.75,
                border: "1px solid rgba(239, 68, 68, 0.2)",
                borderRadius: "10px",
                backgroundColor: "rgba(255, 255, 255, 0.98)",
                boxShadow: "0 8px 24px rgba(17, 24, 39, 0.16)",
              }}
            >
              <Typography variant="caption" sx={{ color: "#374151", whiteSpace: "nowrap" }}>
                Delete all notes?
              </Typography>
              <Button
                size="small"
                onClick={() => setIsClearConfirmationOpen(false)}
                sx={{ minWidth: 0, px: 0.75, color: "#6b7280", textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                aria-label="Confirm deleting all notes"
                size="small"
                onClick={clearNotes}
                sx={{
                  minWidth: 0,
                  px: 0.75,
                  color: "#dc2626",
                  textTransform: "none",
                }}
              >
                Delete
              </Button>
            </Box>
          )}

          {shouldShowToolbar && (
            <Box
              role="toolbar"
              aria-label="Text formatting"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.25,
                py: 0.75,
                borderBottom: "1px solid rgba(17, 24, 39, 0.06)",
                backgroundColor: "rgba(255, 250, 247, 0.7)",
              }}
            >
              <Tooltip title="Bold selected text">
                <IconButton
                  aria-label="Bold selected text"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyFormatting("bold")}
                  size="small"
                  sx={{ color: "#4b5563" }}
                >
                  <FormatBold fontSize="small" />
                </IconButton>
              </Tooltip>

              <Box
                component="label"
                title="Text color"
                sx={{
                  position: "relative",
                  width: 32,
                  height: 32,
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "rgba(249, 115, 22, 0.08)" },
                }}
              >
                <Box component="span" sx={{ color: "#4b5563", fontWeight: 800 }}>A</Box>
                <Box
                  component="input"
                  type="color"
                  aria-label="Text color"
                  defaultValue="#202531"
                  onChange={(event) => applyFormatting("foreColor", event.target.value)}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0.01,
                    cursor: "pointer",
                  }}
                />
              </Box>

              <Box
                component="select"
                aria-label="Text size"
                defaultValue="3"
                onChange={(event) => applyFormatting("fontSize", event.target.value)}
                sx={{
                  height: 30,
                  minWidth: 86,
                  px: 0.75,
                  border: "1px solid rgba(17, 24, 39, 0.12)",
                  borderRadius: "8px",
                  color: "#4b5563",
                  backgroundColor: "#fff",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <option value="2">Small</option>
                <option value="3">Normal</option>
                <option value="5">Large</option>
                <option value="6">Extra large</option>
              </Box>

              <Tooltip title="Hide text settings">
                <IconButton
                  aria-label="Hide text settings"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setIsToolbarVisible(false)}
                  size="small"
                  sx={{
                    ml: "auto",
                    width: 24,
                    height: 24,
                    p: 0,
                    color: "#8b95a1",
                    "&:hover": {
                      color: "#4b5563",
                      backgroundColor: "rgba(17, 24, 39, 0.04)",
                    },
                  }}
                >
                  <KeyboardArrowUp sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {!isToolbarVisible && (
            <Tooltip title="Show text settings">
              <IconButton
                aria-label="Show text settings"
                onClick={() => setIsToolbarVisible(true)}
                size="small"
                sx={{
                  position: "absolute",
                  top: 52,
                  right: 7,
                  zIndex: 1,
                  width: 24,
                  height: 24,
                  p: 0,
                  color: "#8b95a1",
                  "&:hover": {
                    color: "#4b5563",
                    backgroundColor: "rgba(17, 24, 39, 0.04)",
                  },
                }}
              >
                <KeyboardArrowDown sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

            <Box
                ref={editorRef}
                component="div"
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-label="Notes"
                aria-multiline="true"
                data-placeholder="Write a reminder, paste an ID, or keep track of what you’re working on…"
                onInput={syncNotesFromEditor}
                onSelect={saveSelection}
                onKeyUp={saveSelection}
                onMouseUp={saveSelection}
                onPaste={pastePlainText}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  px: 2,
                  py: 1.5,
                  color: "#202531",
                  fontSize: "0.95rem",
                  lineHeight: 1.65,
                  outline: "none",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  "&:empty::before": {
                    content: "attr(data-placeholder)",
                    color: "#9ca3af",
                    pointerEvents: "none",
                  },
                }}
            />

            {!isCompactLayout && (
              <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    px: 2,
                    pr: 5,
                    py: 1,
                    borderTop: "1px solid rgba(17, 24, 39, 0.06)",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                    {getNotesText(notes).length.toLocaleString()} characters
                  </Typography>
              </Box>
            )}

            <IconButton
                aria-label="Resize notes"
                title="Drag to resize notes"
                onPointerDown={(event) => startInteraction(event, "resize")}
                onPointerMove={updateInteraction}
                onPointerUp={endInteraction}
                onPointerCancel={endInteraction}
                onKeyDown={resizeWithKeyboard}
                size="small"
                sx={{
                  position: "absolute",
                  right: 2,
                  bottom: 2,
                  width: 32,
                  height: 32,
                  color: "#b4bac4",
                  cursor: "nwse-resize",
                  touchAction: "none",
                  borderRadius: "9px",
                  "&:hover": {
                    color: "#f97316",
                    backgroundColor: "rgba(249, 115, 22, 0.08)",
                  },
                }}
              >
                <OpenInFull
                  sx={{ fontSize: 17, transform: "rotate(90deg)" }}
                />
            </IconButton>
        </Paper>
      )}

      <Box
        sx={{
          position: "fixed",
          right: { xs: 16, sm: 24 },
          bottom: { xs: 16, sm: 24 },
          zIndex: 1300,
        }}
      >
        <Tooltip title={isOpen ? "Close notes" : "Open notes"} placement="left">
          <Box sx={{ position: "relative" }}>
          <IconButton
            aria-label={isOpen ? "Close notes" : "Open notes"}
            aria-expanded={isOpen}
            onMouseEnter={keepLauncherVisible}
            onMouseLeave={scheduleLauncherFade}
            onFocus={keepLauncherVisible}
            onBlur={scheduleLauncherFade}
            onClick={() => {
              keepLauncherVisible();
              setIsOpen((open) => !open);
            }}
            sx={{
              width: 58,
              height: 58,
              opacity: isLauncherIdle ? 0.16 : 1,
              color: "#fff",
              background: "linear-gradient(135deg, #ff8a3d, #f56a13)",
              border: "1px solid rgba(255, 255, 255, 0.55)",
              boxShadow: "0 12px 28px rgba(239, 108, 28, 0.34)",
              transition: "opacity 0.35s ease, transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #ff934d, #ef6210)",
                boxShadow: "0 16px 34px rgba(239, 108, 28, 0.4)",
                transform: "translateY(-2px)",
              },
              "&:focus-visible": {
                outline: "3px solid rgba(249, 115, 22, 0.28)",
                outlineOffset: 3,
              },
            }}
          >
            {isOpen ? <Close /> : <EditNoteOutlined />}
          </IconButton>
          </Box>
        </Tooltip>
      </Box>
    </>
  );
};

export { NOTES_FORMAT_STORAGE_KEY, NOTES_STORAGE_KEY };
export default FloatingNotes;
